<script setup>
import { displayGameName } from '../../../shared/game-display-name.mjs'
import { gameChoiceLabel } from '../services/game-label'
import { reportGameGroups, reportGameIds } from '../../../shared/report-game-groups.mjs'
import { reportBoundary, dayStart, dayEnd } from '../../../shared/business-day.js'
/**
 * 「游戏记录」页，逐条对齐旧版 web/11.a64382d6d62eab96aad8.js 里的
 * app-bet 路由页（组件类在模块 deGC，模板+样式在模块 x3xv：工厂 w + 子模板 h/m/d/f/_/v/k/C/x/O、样式 g）。
 *
 * 注意这跟牌九弹窗里的「下注记录」（chunk 19 的 app-bet）是**两个不同的组件**，差异：
 *   1. 这里多一个「游戏:」筛选（gameLists 来自 /game/lists）
 *   2. 结算状态的 option 值没有尾空格（这里是 "-1"/"1"/"0"，弹窗版写成 "-1 "/"1 "/"0 "）
 *   3. 明细行多一行 div.game-title「{{game_title}} - {{game_type}}」，
 *      玩法那行显示的是「{{type_title}} - {{play_title}}」
 *   4. .bet-list-item 挂在 ion-label 上（弹窗版挂在普通 div 上）
 *   5. startTime / endTime 默认是空串（查全部时间），弹窗版默认today 00:00~23:59
 *
 * 旧版构造函数会读 queryParams：
 *   game  → gameId
 *   start → moment(start).format('YYYY-MM-DDT00:00:00')
 *   end   → moment(end).format('YYYY-MM-DDT23:59:59')
 *   state → lotteryState
 *   并立即 getGameList()
 * ngOnInit → getBetLists()
 *
 * getBetLists(l): POST /game/bet/lists {game, lottery, page, start, end}
 *   lists = page_index === 1 ? data.lists : lists.concat(data.lists)
 * cancelAll(row, index): alert「确定取消该注单吗？」→ POST /game/bet/cancel {game, game_no, play}
 *   成功 → toast「取消成功」+ lists.splice(index, 1)
 */
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { request } from '../services/api'
import PullRefresher from '../components/PullRefresher.vue'
import LegacySelect from '../components/LegacySelect.vue'
import LegacyAlert from '../components/LegacyAlert.vue'
import AppBottomNav from '../components/AppBottomNav.vue'

const route = useRoute()
const backTarget = computed(() => route.query.from === 'bill'
  ? { path: '/game/bill', query: { game: String(route.query.game || '0') } }
  : '/user/profile')

const paramShow = ref(true)
// 旧版默认值：gameId='0'（不限）、lotteryState='-1'（不限）
const gameId = ref('0')
const lotteryState = ref('-1')
const gameLists = ref([])
const lists = ref(null)
const page = ref({ item_total: 0, page_index: 1, page_size: 15, page_count: 0 })
const loading = ref(false)
const notice = ref('')
const confirmTarget = ref(null)
let listRequestVersion = 0

/** 旧版构造函数里对 query 的处理：有 start/end 才格式化，否则保持空串 */
function toLocalIso(value, timePart) {
  if (!value) return ''
  const date = new Date(String(value).replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return ''
  const pad = (number) => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${timePart}`
}
const startTime = ref(reportBoundary(route.query.start))
const endTime = ref(reportBoundary(route.query.end, true))
if (route.query.game) gameId.value = String(route.query.game)
if (route.query.state) lotteryState.value = String(route.query.state)

const hasMore = computed(() => page.value.page_count > 1 && page.value.page_count != page.value.page_index)

/** 旧版「游戏:」的选项：固定「不限」+ gameLists */
const gameOptions = computed(() => {
  const options = [{ value: '0', label: '不限' }, ...reportGameGroups.map(({ value, label }) => ({ value, label }))]
  gameLists.value.forEach((game) => { options.push({ value: String(game.game_id), label: gameChoiceLabel(game) }) })
  return options
})

// 输赢分类仅查询已结算注单，以不含返水的盈利为准。
const lotteryOptions = [
  { value: '-1', label: '不限' },
  { value: '1', label: '已结算' },
  { value: '0', label: '未结算' },
  { value: 'win', label: '所赢明细' },
  { value: 'loss', label: '所输明细' },
  { value: 'draw', label: '所和明细' },
]

// 旧版 DecimalPipe '.2-2'
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }

/** 旧版 [ngClass]="{win: x>0, lost: x<0}" */
function signClass(value) { return { win: Number(value) > 0, lost: Number(value) < 0 } }

/** 旧版 {{ bet_create_time | amDateFormat:'MM-DD HH:mm' }} */
function fmtTime(value) {
  if (!value) return ''
  const raw = String(value)
  const date = new Date(/^\d+$/.test(raw) ? Number(raw) * 1000 : raw.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return raw
  const pad = (number) => String(number).padStart(2, '0')
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** 旧版 getGameList()，构造函数里就调用 */
async function getGameList() {
  try {
    const response = await request('/game/lists')
    gameLists.value = response.data?.lists || []
    return true
  } catch (error) {
    notice.value = error.message || '加载失败'
    return false
  }
}

/** 旧版 getBetLists(l=null) */
async function getBetLists(event = null) {
  const version = ++listRequestVersion
  const pageIndex = page.value.page_index
  const result = ['win', 'loss', 'draw'].includes(lotteryState.value) ? lotteryState.value : ''
  loading.value = true
  notice.value = ''
  try {
    const response = await request('/game/bet/lists', {
      method: 'POST',
      data: {
        game: reportGameIds(gameId.value, gameLists.value),
        lottery: result ? '1' : lotteryState.value,
        result,
        page: pageIndex,
        start: startTime.value,
        end: endTime.value,
      },
    })
    if (version !== listRequestVersion) return
    page.value = { ...page.value, ...(response.page || {}), page_index: pageIndex }
    const incoming = response.data?.lists || []
    lists.value = page.value.page_index === 1 ? incoming : [...(lists.value || []), ...incoming]
  } catch (error) {
    if (version === listRequestVersion) {
      notice.value = error.message || '加载失败'
      if (pageIndex > 1) page.value = { ...page.value, page_index: pageIndex - 1 }
    }
  } finally {
    if (version === listRequestVersion) loading.value = false
    event?.complete()
  }
}

/** 旧版 changParam() */
function changParam() {
  lists.value = null
  page.value = { ...page.value, page_index: 1, page_count: 0, item_total: 0 }
  void getBetLists()
}

/** 旧版 doRefresh(l) */
function doRefresh(event) {
  page.value = { ...page.value, page_index: 1 }
  void getBetLists(event)
}

/** 旧版 doInfinite(l) */
function doInfinite() {
  if (loading.value) return
  const next = page.value.page_index + 1
  if (next > page.value.page_count) { notice.value = '已经最后一页了'; return }
  page.value = { ...page.value, page_index: next }
  void getBetLists()
}

/** 旧版 cancelAll(row, index) 的确认 alert */
function askCancel(row, index) { confirmTarget.value = { row, index } }

async function doCancel() {
  const target = confirmTarget.value
  confirmTarget.value = null
  if (!target) return
  const { row, index } = target
  try {
    await request('/game/bet/cancel', {
      method: 'POST',
      data: { game: row.game_id, game_no: row.game_no, play: row.play_id },
    })
    notice.value = '取消成功'
    if (index >= 0 && lists.value) lists.value.splice(index, 1)
  } catch (error) {
    notice.value = error.message || '取消失败'
  }
}

// 分类查询需要先根据可用游戏列表解析游戏编号。
onMounted(async () => { if (await getGameList()) await getBetLists() })
</script>

<template>
  <div class="bet-record-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮 routerLink="/tabs/user" -->
    <header class="modal-header">
      <RouterLink class="modal-back" :to="backTarget" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">游戏记录</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <PullRefresher
      pulling-text="下拉刷新"
      refreshing-text="下拉刷新..."
      refreshing-spinner="circles"
      @refresh="doRefresh"
    >
      <!-- 旧版第一个 ion-list（模板里写死 class="list-ios"，但 md 模式下这个类没有对应样式，
           属于旧版模板的残留，这里保留类名不赋样式） -->
      <section class="ion-list list-ios">
        <div class="list-header" @click="paramShow = !paramShow">
          <span> 筛选 </span>
          <svg v-if="paramShow" class="header-arrow" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 392L100 236l28-28 128 128 128-128 28 28z" fill="currentColor" /></svg>
          <svg v-else class="header-arrow" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 120l156 156-28 28-128-128-128 128-28-28z" fill="currentColor" /></svg>
        </div>

        <!-- 旧版 f：*ngIf="paramShow && gameLists" -->
        <div v-if="paramShow && gameLists.length" class="list-item">
          <div class="list-item-inner">
            <div class="ion-label">游戏:</div>
            <LegacySelect
              v-model="gameId"
              :options="gameOptions"
              wrap-labels
              alert-header="游戏:"
              cancel-text="取消"
              ok-text="确定"
              @change="changParam"
            />
          </div>
        </div>

        <!-- 旧版 _：*ngIf="paramShow" -->
        <div v-if="paramShow" class="list-item">
          <div class="list-item-inner">
            <div class="ion-label">结算状态:</div>
            <LegacySelect
              v-model="lotteryState"
              :options="lotteryOptions"
              alert-header="结算状态:"
              cancel-text="取消"
              ok-text="确定"
              @change="changParam"
            />
          </div>
        </div>
      </section>

      <!-- 旧版 x：*ngIf="lists" 的明细列表 -->
      <section v-if="lists && lists.length" class="ion-list list-ios">
        <div class="list-header"> 明细 </div>
        <div v-for="(row, index) in lists" :key="row.bet_id ?? index" class="list-item">
          <div class="list-item-inner">
            <!-- 旧版这里 .bet-list-item 是挂在 ion-label 上的 -->
            <div class="ion-label bet-list-item">
              <div class="game-info">
                <div class="game-title"> {{ row.game_title }} - {{ displayGameName(row.game_type) }} </div>
                <div class="game-no"> {{ row.game_no }} </div>
                <div class="bet-time"> {{ fmtTime(row.bet_create_time) }} </div>
              </div>
              <div class="bet-info">
                <div class="info-item"><span class="info-title"> 玩法: </span><span class="info-value"> {{ displayGameName(row.type_title) }} - {{ row.play_title }} </span></div>
                <div class="info-item"><span class="info-title"> 赔率: </span><span class="info-value"> {{ row.bet_odds }} </span></div>
                <div class="info-item"><span class="info-title"> 金额: </span><span class="info-value"> {{ fmt2(row.bet_money) }} </span></div>
              </div>
              <div class="bet-action">
                <!-- 旧版 v：*ngIf="bet_lottery_state == 0" -->
                <div v-if="row.bet_lottery_state == 0" class="info-item">
                  <button class="ion-button button-small button-solid ion-color-danger" type="button" @click="askCancel(row, index)">
                    <span class="button-inner">取消</span>
                  </button>
                </div>
                <!-- 旧版 k：*ngIf="bet_lottery_state == 1" -->
                <template v-else-if="row.bet_lottery_state == 1">
                  <div class="info-item">
                    <span class="info-title"> 中奖: </span>
                    <span class="info-value" :class="signClass(row.bet_money_win)"> {{ fmt2(row.bet_money_win) }} </span>
                  </div>
                  <div class="info-item">
                    <span class="info-title"> 反水: </span>
                    <span class="info-value"> {{ fmt2(row.bet_money_back) }} </span>
                  </div>
                  <div class="info-item">
                    <span class="info-title"> 盈利: </span>
                    <span class="info-value" :class="signClass(row.bet_money_pl)"> {{ fmt2(row.bet_money_pl) }} </span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 旧版 O：ion-infinite-scroll，loadingSpinner="bubbles" loadingText="正在加载" -->
      <button v-if="hasMore" class="load-more" type="button" :disabled="loading" @click="doInfinite">
        正在加载
      </button>
      <p v-if="loading" class="modal-notice" role="status">正在加载…</p>
      <p v-else-if="!notice && lists && !lists.length" class="modal-notice" role="status">当前筛选条件下暂无记录</p>
      <p v-if="notice" class="modal-notice" role="alert">{{ notice }}</p>
    </PullRefresher>

    <!-- 旧版 cancelAll 的确认 alert -->
    <Teleport to="body">
      <LegacyAlert
        v-if="confirmTarget"
        header="确认"
        message="确定取消该注单吗？"
        cancel-text="取消"
        ok-text="确认"
        @cancel="confirmTarget = null"
        @confirm="doCancel"
      />
    </Teleport>

    <!-- 这一页就是旧版底部导航第二个 tab「投注概况」指向的 /tabs/bet
         （见 chunk 34 的路由表：tabs 下 bet → ../game/bet/bet.module#BetPageModule），
         个人中心的「游戏记录」入口跳的也是同一个路由，两个入口都在 tabs 里，
         所以 tab 栏一直挂着 -->
    <AppBottomNav />
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
/* padding-bottom 是给底部 tab 栏（fixed，56px）留位 */
.bet-record-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif;padding-bottom:56px;box-sizing:border-box}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-header{display:flex;align-items:center;justify-content:space-between;width:100%;margin:0;padding:0 16px;min-height:45px;font-size:14px;background:transparent;color:#000;overflow:hidden}
.list-header .header-arrow{width:1em;height:1em;flex:none;opacity:.54}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}

/* --- 以下逐条照抄旧版样式表 g（chunk 11 的模块 x3xv），:host → .bet-record-page --- */
.lists-header{justify-content:flex-start}
.ion-list .info-title{color:#666;display:inline-block;margin-right:10px}
.ion-list .info-value{display:inline-block}
.ion-list .win{color:red}
.ion-list .lost{color:green}
.ion-list .bet-list-item{padding-top:5px;padding-bottom:5px;width:100%;font-size:.8rem;display:flex;align-items:center;justify-content:space-between}
.ion-list .bet-list-item .game-info{width:33%}
.ion-list .bet-list-item .bet-info{width:33%}
.ion-list .bet-list-item .bet-action{width:33%}
/* .bet-list-item 挂在 ion-label 上，需要放开 ion-label 的单行截断，否则三块会被压成一行 */
.ion-label.bet-list-item{white-space:normal;text-overflow:clip;overflow:visible}

/* ion-button color="danger" size="small"，取自 web/75（md） */
.ion-button{
  display:inline-flex;align-items:center;justify-content:center;
  margin:4px 2px;border:0;border-radius:4px;
  font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  height:2.1em;font-size:13px;padding:0 .9em;
  background:var(--ion-color-danger,#f04141);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

.load-more{display:block;width:calc(100% - 32px);margin:10px 16px;padding:8px 0;border:1px solid #ddd;border-radius:4px;background:#fafafa;font-size:.8rem}
.modal-notice{margin:10px 16px;color:#999;font-size:.8rem;text-align:center}
</style>
